/**
 * Task 9: Panels with Cutouts/Custom Apps
 * Render panels with custom appliances or cutouts to the right side of kitchen view.
 * Only triggered by custom appliances and cutouts (excluding utility cutouts), not regular appliances.
 */
import React, { useContext, useMemo } from 'react';

import { type Island, type Panel, type PanelItem, type PanelItemGroup } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core';
import { DraggableCustomPanelCard, LEGEND_LINE_HEIGHT, LEGEND_PADDING, PANEL_CARD_WIDTH, TITLE_AREA_HEIGHT } from './draggable-custom-panel-card';
import { renderTracking } from '@r-services/render-tracking';

export type RenderCustomPanelsSideViewProps = {
	islands: Array<Island>;
	kitchenWidth: number;
};

const PANEL_CARD_MARGIN = 6;
const PANEL_CARD_OFFSET_X = 3;
const PANEL_CARD_START_Y = 5;
// The visual rendering is roughly this multiplier of panel.size (surround, countertop, vents, dims)
const VISUAL_SIZE_MULTIPLIER_W = 1.4;
const VISUAL_SIZE_MULTIPLIER_H = 1.65; // used for panel positioning (keeps panel centered)
const CARD_HEIGHT_MULTIPLIER = 1.35;   // used for card height (tighter fit)

/**
 * Checks if an item is a utility cutout (should NOT trigger side panel)
 */
const isUtilityCutout = (panelItem: PanelItem): boolean => {
	return panelItem.item.category?.isUtilityCutout ?? false;
};

/**
 * Checks if a panel has any non-utility cutouts
 * Utility cutouts and custom appliances should NOT trigger the side panel
 */
const panelHasCustomItems = (panel: Panel): boolean => {
	let hasCustomItems = false;

	panel.panelItemGroups.children.forEach((panelItemGroup: PanelItemGroup) => {
		if (panelItemGroup.hasPassthrough) return;

		panelItemGroup.items.children.forEach((panelItem: PanelItem) => {
			if (isUtilityCutout(panelItem)) return;
			const { sku } = panelItem.item;
			if (sku === 'CUTOUT') {
				hasCustomItems = true;
			}
		});
	});

	return hasCustomItems;
};

/**
 * Gets the cutout items from a panel (excludes utility cutouts and custom appliances)
 */
const getCustomItemsFromPanel = (panel: Panel): Array<PanelItem> => {
	const customItems: Array<PanelItem> = [];

	panel.panelItemGroups.children.forEach((panelItemGroup: PanelItemGroup) => {
		if (panelItemGroup.hasPassthrough) return;

		panelItemGroup.items.children.forEach((panelItem: PanelItem) => {
			if (isUtilityCutout(panelItem)) return;
			const { sku } = panelItem.item;
			if (sku === 'CUTOUT') {
				customItems.push(panelItem);
			}
		});
	});

	return customItems;
};

/**
 * RenderCustomPanelsSideView Component
 * Renders panels with custom appliances or cutouts to the right side of the kitchen view.
 * Only renders when SKU layer is active.
 */
export const RenderCustomPanelsSideView = (props: RenderCustomPanelsSideViewProps): React.ReactElement => {
	const { islands, kitchenWidth } = props;
	const { controller } = useContext(AppContext);
	const { navigation, options } = controller;
	const fontSizeFactor = options?.dimensionFontSizeFactor ?? 1;
	const scaledLegendLineHeight = LEGEND_LINE_HEIGHT * fontSizeFactor;

	// Collect all panels with custom items across all islands (must be called before any conditional returns)
	const panelsWithCustomItems = useMemo(() => {
		const panels: Array<{ panel: Panel; island: Island; customItems: Array<PanelItem> }> = [];

		islands.forEach((island) => {
			island.panels.children.forEach((panel: Panel) => {
				if (panelHasCustomItems(panel)) {
					panels.push({
						panel,
						island,
						customItems: getCustomItemsFromPanel(panel)
					});
				}
			});
		});

		return panels;
	}, [islands]);

	renderTracking.incrementRender('RenderCustomPanelsSideView');

	// Only render when SKU layer is active or in SKU Overhead mode
	const isSkuLayerActive = navigation.displayMode === DisplayMode.SkuOverhead || navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
	if (!isSkuLayerActive) {
		return <></>;
	}

	if (panelsWithCustomItems.length === 0) {
		return <></>;
	}

	// Calculate starting X position (right side of kitchen)
	const startX = (kitchenWidth / 2) + PANEL_CARD_OFFSET_X;

	// Pre-calculate card layouts
	const cardLayouts = panelsWithCustomItems.map(({ panel, customItems }) => {
		const visualWidth = panel.size.width * VISUAL_SIZE_MULTIPLIER_W;

		const legendHeight = LEGEND_PADDING * 2 + customItems.length * scaledLegendLineHeight;

		// Scale factor to fit the visual content in the fixed card width
		const panelAreaWidth = PANEL_CARD_WIDTH - 8;
		const scaleFactor = Math.min(panelAreaWidth / visualWidth, 1);

		// Panel positioning uses VISUAL_SIZE_MULTIPLIER_H (keeps panel in correct spot)
		const scaledContentHeight = panel.size.height * VISUAL_SIZE_MULTIPLIER_H * scaleFactor;
		// Card height uses CARD_HEIGHT_MULTIPLIER (tighter fit, less whitespace)
		const scaledCardContentHeight = panel.size.height * CARD_HEIGHT_MULTIPLIER * scaleFactor;
		const cardHeight = TITLE_AREA_HEIGHT + scaledCardContentHeight + legendHeight - 10;

		return { cardHeight, legendHeight, scaleFactor, scaledContentHeight };
	});

	return (
		<g type="custom-panels-side-view">
			{panelsWithCustomItems.map(({ panel, island, customItems }, index) => {
				const { cardHeight, legendHeight, scaleFactor, scaledContentHeight } = cardLayouts[index];

				const cardX = startX;
				let cardY = PANEL_CARD_START_Y;
				for (let i = 0; i < index; i++) {
					cardY += cardLayouts[i].cardHeight + PANEL_CARD_MARGIN;
				}

				return (
					<DraggableCustomPanelCard
						key={`custom-panel-${panel.id}`}
						panel={panel}
						island={island}
						customItems={customItems}
						cardX={cardX}
						cardY={cardY}
						cardHeight={cardHeight}
						legendHeight={legendHeight}
						scaleFactor={scaleFactor}
						scaledContentHeight={scaledContentHeight}
					/>
				);
			})}
		</g>
	);
};
